import React, {
  createContext,
  useContext,
  ReactNode,
  useEffect,
  useState,
  useCallback,
} from "react";
import { useAuth, AuthState } from "react-oidc-context";
import { apiService, authService, clientService } from "../services";
import { ClientDto } from "@shared/types/ClientDto";

const EMAIL_AUTH_STORAGE_KEY = "echopeak_email_auth";

interface EmailAuthData {
  accessToken: string;
  idToken: string;
  refreshToken: string;
  expiresAt: number;
}

interface EmailAuthTokens {
  accessToken: string;
  idToken: string;
  refreshToken: string;
  expiresIn: number;
}

interface AuthContextType {
  loginWithOIDCProvider: (provider: SupportedAuthProvider) => Promise<void>;
  loginWithEmail: (
    email: string,
    password: string,
    turnstileToken: string,
  ) => Promise<void>;
  completeEmailLogin: (tokens: EmailAuthTokens) => void;
  logout: () => void;
  isAuthenticated: boolean;
  isLoading: boolean;
  userId: string | null;
  user: AuthState["user"] | null;
  clientInfo: ClientDto | null;
  clientInfoLoading: boolean;
  getClientInfo: () => Promise<void>;
  sendVerificationCode: (email: string) => Promise<void>;
  confirmVerificationCode: (email: string, code: string) => Promise<void>;
  checkEmailVerificationStatus: (email: string) => Promise<boolean>;
}

export enum SupportedAuthProvider {
  GOOGLE = "Google",
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const auth = useAuth();
  const [emailAuth, setEmailAuth] = useState<EmailAuthData | null>(() => {
    const stored = localStorage.getItem(EMAIL_AUTH_STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored) as EmailAuthData;
        if (parsed.expiresAt > Date.now()) {
          return parsed;
        }
        localStorage.removeItem(EMAIL_AUTH_STORAGE_KEY);
      } catch {
        localStorage.removeItem(EMAIL_AUTH_STORAGE_KEY);
      }
    }
    return null;
  });
  const [emailAuthLoading, setEmailAuthLoading] = useState(false);
  const [clientInfo, setClientInfo] = useState<ClientDto | null>(null);
  const [clientInfoLoading, setClientInfoLoading] = useState(false);

  const loginWithOIDCProvider = useCallback(
    async (provider: SupportedAuthProvider) => {
      try {
        console.debug("[OIDC] Starting signinRedirect for provider:", provider);
        console.debug(
          "[OIDC] Authority:",
          import.meta.env.VITE_COGNITO_AUTHORITY,
        );
        console.debug(
          "[OIDC] Client ID:",
          import.meta.env.VITE_COGNITO_CLIENT_ID,
        );
        console.debug(
          "[OIDC] Redirect URI:",
          `${window.location.origin}/auth/signin-callback`,
        );
        await auth.signinRedirect({
          extraQueryParams: {
            identity_provider: provider,
          },
        });
      } catch (error) {
        console.error("[OIDC] signinRedirect failed:", error);
        if (error instanceof Error) {
          console.error("[OIDC] Error details:", error.message, error.stack);
        }
      }
    },
    [auth],
  );

  const completeEmailLogin = useCallback((tokens: EmailAuthTokens): void => {
    const authData: EmailAuthData = {
      accessToken: tokens.accessToken,
      idToken: tokens.idToken,
      refreshToken: tokens.refreshToken,
      expiresAt: Date.now() + tokens.expiresIn * 1000,
    };
    localStorage.setItem(EMAIL_AUTH_STORAGE_KEY, JSON.stringify(authData));
    setEmailAuth(authData);
    apiService.setAccessToken(authData.idToken);
    apiService.setAuthReady(true);
  }, []);

  const loginWithEmail = useCallback(
    async (
      email: string,
      password: string,
      turnstileToken: string,
    ): Promise<void> => {
      setEmailAuthLoading(true);
      try {
        const response = await authService.loginWithEmail({
          email,
          password,
          turnstileToken,
        });
        if (
          response.accessToken &&
          response.idToken &&
          response.refreshToken &&
          response.expiresIn
        ) {
          completeEmailLogin({
            accessToken: response.accessToken,
            idToken: response.idToken,
            refreshToken: response.refreshToken,
            expiresIn: response.expiresIn,
          });
        }
      } finally {
        setEmailAuthLoading(false);
      }
    },
    [completeEmailLogin],
  );

  const getClientInfo = useCallback(async () => {
    setClientInfoLoading(true);
    try {
      const info = await clientService.getClientInfo();
      setClientInfo(info);
    } catch (error) {
      console.error("Failed to fetch client info:", error);
      setClientInfo(null);
    } finally {
      setClientInfoLoading(false);
    }
  }, []);

  const sendVerificationCode = useCallback(
    async (email: string): Promise<void> => {
      const response = await authService.sendEmailVerificationCode({
        email,
      });
      if (!response.success && !response.alreadyVerified) {
        throw new Error("Failed to send verification code");
      }
    },
    [],
  );

  const confirmVerificationCode = useCallback(
    async (email: string, code: string): Promise<void> => {
      const response = await authService.confirmEmailVerificationCode({
        email,
        code,
      });
      if (!response.success) {
        throw new Error("Verification failed");
      }
    },
    [],
  );

  const checkEmailVerificationStatus = useCallback(
    async (email: string): Promise<boolean> => {
      const response = await authService.checkEmailVerificationStatus(email);
      return response.verified;
    },
    [],
  );

  const logout = () => {
    const mainHost = window.location.hostname.replace(/-?portal\./, "");
    const port = window.location.port ? `:${window.location.port}` : "";
    const publicOrigin = `${window.location.protocol}//${mainHost}${port}`;

    if (emailAuth) {
      localStorage.removeItem(EMAIL_AUTH_STORAGE_KEY);
      apiService.setAccessToken(null);
      window.location.href = `${publicOrigin}/`;
      return;
    }

    const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID;
    const cognitoDomain = import.meta.env.VITE_COGNITO_HOSTED_UI_DOMAIN;
    const logoutUri = `${publicOrigin}/auth/signout-callback`;

    window.location.href =
      `${cognitoDomain}/logout?` +
      `client_id=${encodeURIComponent(clientId)}` +
      `&logout_uri=${encodeURIComponent(logoutUri)}`;
  };

  const isLoading = auth.isLoading || emailAuthLoading;

  const isEmailAuthenticated =
    emailAuth !== null && emailAuth.expiresAt > Date.now();
  const isAuthenticated =
    !isLoading && (auth.isAuthenticated || isEmailAuthenticated);
  const userId = emailAuth?.idToken || auth.user?.id_token || null;

  useEffect(() => {
    if (auth.error) {
      console.error("[OIDC] Auth error state:", auth.error.message, auth.error);
    }
  }, [auth.error]);

  useEffect(() => {
    const mgr = auth.settings ? (auth as any).userManager : null;
    if (!mgr?.events) return;

    const onError = (err: Error) =>
      console.error("[OIDC] Silent renew error:", err.message, err);
    const onExpired = () => console.warn("[OIDC] Access token expired");
    const onExpiring = () => console.warn("[OIDC] Access token expiring soon");
    const onUnloaded = () => console.warn("[OIDC] User session unloaded");

    mgr.events.addSilentRenewError(onError);
    mgr.events.addAccessTokenExpired(onExpired);
    mgr.events.addAccessTokenExpiring(onExpiring);
    mgr.events.addUserUnloaded(onUnloaded);

    return () => {
      mgr.events.removeSilentRenewError(onError);
      mgr.events.removeAccessTokenExpired(onExpired);
      mgr.events.removeAccessTokenExpiring(onExpiring);
      mgr.events.removeUserUnloaded(onUnloaded);
    };
  }, [auth]);

  useEffect(() => {
    if (emailAuth) {
      apiService.setAccessToken(emailAuth.idToken);
    } else {
      const idToken = auth.user?.id_token || null;
      apiService.setAccessToken(idToken);
    }
  }, [auth.user?.id_token, auth.isAuthenticated, emailAuth]);

  useEffect(() => {
    if (!isLoading) {
      apiService.setAuthReady(isAuthenticated);
    }
  }, [isLoading, isAuthenticated]);

  const value = {
    loginWithOIDCProvider,
    loginWithEmail,
    completeEmailLogin,
    logout,
    isAuthenticated,
    isLoading,
    userId,
    user: auth.user,
    clientInfo,
    clientInfoLoading,
    getClientInfo,
    sendVerificationCode,
    confirmVerificationCode,
    checkEmailVerificationStatus,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuthContext = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuthContext must be used within an AuthProvider");
  }
  return context;
};
